
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/components/AuthProvider";
import { supabase } from "@/integrations/supabase/client";

export const useTrainingSessions = () => {
  const { user } = useAuth();

  const { data: studentProfile, isLoading: isLoadingProfile } = useQuery({
    queryKey: ["student-profile-for-sessions", user?.id, user?.email],
    queryFn: async () => {
      if (!user?.id && !user?.email) return null;

      console.log("Buscando perfil do aluno para sessões:", user.email || user.id);
      
      if (user.email) {
        const { data: emailData, error: emailError } = await supabase
          .from("students")
          .select("*")
          .eq("email", user.email)
          .eq("status", "active")
          .maybeSingle();

        if (emailError) {
          console.error("Erro ao buscar aluno pelo email:", emailError);
        }

        if (emailData) {
          console.log("Aluno encontrado pelo email:", emailData.id);
          
          if (user.id && (!emailData.auth_id || emailData.auth_id !== user.id)) {
            console.log("Atualizando auth_id do aluno:", emailData.id);
            const { error: updateError } = await supabase
              .from('students')
              .update({
                auth_id: user.id,
                auth_account_created: true
              })
              .eq('id', emailData.id);
            
            if (updateError) {
              console.error('Erro ao atualizar auth_id do aluno:', updateError);
            }
          }
          
          return emailData;
        }
      }
      
      if (user.id) {
        const { data, error } = await supabase
          .from("students")
          .select("*")
          .eq("auth_id", user.id)
          .eq("status", "active")
          .maybeSingle();

        if (error) {
          console.error("Erro ao buscar aluno pelo auth_id:", error);
        }

        if (data) {
          console.log("Aluno encontrado pelo auth_id:", data.id);
          return data;
        }
      }

      console.log("Aluno não encontrado para sessões de treino.");
      return null;
    },
    enabled: !!(user?.id || user?.email),
    staleTime: 0, // Sempre busque dados frescos
    refetchOnMount: true,
    refetchOnWindowFocus: true
  });

  const { data: sessions, isLoading: isLoadingSessions } = useQuery({
    queryKey: ["student-training-sessions", studentProfile?.id, studentProfile?.trainer_id, user?.id],
    queryFn: async () => {
      if (!studentProfile?.id) return [];

      console.log("Buscando aulas para aluno:", studentProfile.id, "trainer_id:", studentProfile.trainer_id);

      // First get custom exercises to match against
      const { data: customExercises, error: customExerciseError } = await supabase
        .from("custom_exercises")
        .select("*")
        .eq("trainer_id", studentProfile.trainer_id);

      if (customExerciseError) {
        console.error("Erro ao buscar exercícios personalizados:", customExerciseError);
      }

      console.log("Exercícios personalizados encontrados:", customExercises?.length || 0);

      // Create a map for efficient lookup (using lowercase name for case-insensitive matching)
      const customExerciseMap = new Map(
        customExercises?.map(ce => [ce.name.toLowerCase(), ce]) || []
      );

      const { data, error } = await supabase
        .from("training_sessions")
        .select(`
          *,
          workout:workouts(
            id,
            title,
            exercises(*)
          ),
          student:students(
            id,
            first_name,
            last_name
          ),
          trainer:profiles(
            id,
            first_name,
            last_name
          )
        `)
        .eq("student_id", studentProfile.id)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Erro ao buscar aulas:", error);
        return [];
      }

      // Process sessions to correctly mark custom exercises
      const processedSessions = data.map(session => {
        if (session.workout?.exercises) {
          console.log(`Processando ${session.workout.exercises.length} exercícios para a sessão:`, session.id);
          
          session.workout.exercises = session.workout.exercises.map(exercise => {
            // Only process the exercise if it's not an error object
            if (exercise && typeof exercise === 'object' && 'name' in exercise) {
              const exerciseName = exercise.name?.toLowerCase() || '';
              const customExercise = customExerciseMap.get(exerciseName);
              
              if (customExercise) {
                console.log(`Exercício "${exercise.name}" é personalizado:`, {
                  originalImage: exercise.image_url,
                  customImage: customExercise.image_url
                });
                
                return {
                  ...exercise,
                  isCustom: true,
                  image_url: customExercise.image_url,
                  body_part: customExercise.body_part || exercise.body_part,
                  target_muscle: customExercise.target_muscle || exercise.target_muscle
                };
              }
              
              return {
                ...exercise,
                isCustom: false
              };
            }
            
            console.warn("Objeto de exercício inválido:", exercise);
            return exercise;
          }).sort((a, b) => ((a as any).order_index || 0) - ((b as any).order_index || 0));
        }
        return session;
      });

      console.log("Sessões de treino processadas:", processedSessions?.length);
      return processedSessions || [];
    },
    enabled: !!studentProfile?.id,
    staleTime: 0, // Sempre busque dados frescos
    refetchOnMount: true, 
    refetchOnWindowFocus: true
  });

  return { 
    sessions, 
    isLoading: isLoadingProfile || isLoadingSessions,
    studentProfile 
  };
};
